import { ReactNode } from 'react';
import { InView } from 'react-intersection-observer';
import { ProductCard } from '@pu/mpp-ui/components/product-card';
import { SkeletonPlaceHolder } from '@pu/mpp-ui/components/skeleton';
import { useCSS } from '@pu/mpp-ui/hooks';
import { PRODUCT_COUNTER_CLICKING_DELAY } from 'core/constants/catalogue.constants';
import { ImageRoutes } from 'core/constants/image-routes';
import { useProductActions } from 'core/hooks/products/use-product-actions';
import { EProductOrigin } from 'core/model/enums/gtm.enum';
import type { IProduct } from 'core/model/interfaces/product.interface';
import CarouselSkeleton from 'shared/components/products/carousel-skeleton';
import { SlickSlider } from 'shared/components/slick-slider';
import { useNotifyPurchaseIncrement } from 'shared/hooks/use-notify-purchase-increment.hook';
import { mapProductToCardProductData } from 'shared/utils/products.utils';
import { useCarouselProductContext } from '../../carousel-products.context';
import { useCarouselProductsData } from '../../use-carousel-products-data.hooks';

import './carousel-products-body.scss';

export interface ICarouselProductBodyProps {
  listName?: string;
  origin: EProductOrigin;
  onViewCarousel?: (products: Array<IProduct>) => void;
  children?: ReactNode;
}

export const CarouselProductsBody = ({
  listName = 'null',
  origin,
  children,
  onViewCarousel,
}: ICarouselProductBodyProps) => {
  const css = useCSS('carousel-products-body');
  const { notifiedProducts, onNotify, purchaseIncrementAppliedHandler } = useNotifyPurchaseIncrement();
  const { cartProducts } = useCarouselProductsData();
  const { variant = 'default', products, title, shouldSeeMore } = useCarouselProductContext();
  const { onAbort, onChangeCounterQuantity, onDetailProduct } = useProductActions({
    listName,
    origin,
  });

  const renderProductCards = () => {
    const productCards = products.map((item, index) => {
      const cartProduct = cartProducts?.find(p => p.sku === item.sku);
      const quantity = cartProduct?.quantity ?? 0;
      const {
        product: cardProductData,
        unitMeasureCode,
        tags,
        infoLine,
        infoLabel,
      } = mapProductToCardProductData({
        product: item,
        cartProduct,
      });

      return (
        <ProductCard
          className={css('product-card')}
          clickingDelay={PRODUCT_COUNTER_CLICKING_DELAY}
          defaultImageSrc={ImageRoutes.CATALOGUE_DEFAULT_PRODUCT}
          isLoading={false}
          key={`product-card-${item.sku}-${title}`}
          onAbort={() => onAbort(item.sku, unitMeasureCode)}
          onCardClick={() => onDetailProduct(item)}
          onChangeQuantity={newQuantity =>
            onChangeCounterQuantity({
              item,
              currentQuantity: quantity,
              newQuantity,
              unitMeasureCode,
              index,
              metadata: cartProduct?.metadata,
              stock: item.stock,
            })
          }
          nutritionalWarningsImgSrc={item.octagonsImageUrl}
          product={cardProductData}
          quantity={quantity}
          tags={tags}
          variant={variant}
          active={cardProductData.active}
          infoLine={infoLine}
          infoLabel={infoLabel}
          onPurchaseIncrementApplied={purchaseIncrementAppliedHandler({
            purchaseIncrement: cardProductData.purchaseIncrement,
            notificationsFired: notifiedProducts[item.sku],
            onNotify: () => onNotify(item.sku),
          })}
        />
      );
    });

    if (children && shouldSeeMore) {
      productCards.push(children as JSX.Element);
    }

    return productCards;
  };

  return (
    <InView
      as="div"
      aria-live="polite"
      aria-label="lista de productos"
      triggerOnce
      onChange={inView => inView && onViewCarousel?.(products)}
      className={css()}
    >
      <SkeletonPlaceHolder
        loading={!products?.length}
        skeleton={<CarouselSkeleton ariaLabel="cargando productos" variant={variant} />}
      >
        <SlickSlider items={renderProductCards()} />
      </SkeletonPlaceHolder>
    </InView>
  );
};
